import React, { useEffect, useState } from "react";
import axios from "axios";
import {
  Box,
  Typography,
  TextField,
  Button,
  CircularProgress,
  Alert,
  Paper,
  Stack,
  Container,
  Breadcrumbs,
  Fade,
  Divider,
  Card,
  CardContent,
  ListItemIcon,
  ListItemText,
} from "@mui/material";

// アイコン
import SettingsIcon from "@mui/icons-material/Settings";
import HomeIcon from "@mui/icons-material/Home";
import PeopleIcon from "@mui/icons-material/People";
import LockIcon from "@mui/icons-material/Lock";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import RefreshIcon from "@mui/icons-material/Refresh";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import SecurityIcon from '@mui/icons-material/Security';

import Header from "../../../components/Header";
import { useNavigate } from "react-router-dom";

const SettingsSharedCode = () => {
  const [code, setCode] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const [sharedView, setSharedView] = useState(null); // 初期状態（読み込み中）
  const navigate = useNavigate();

  // ✅ 初回アクセス時に共有状態を確認し、共有中ならリダイレクト
  useEffect(() => {
    const checkSharedStatus = async () => {
      try {
        const res = await fetch("/api/shared/view", {
          headers: {
            Authorization: "Bearer " + sessionStorage.getItem("token"),
          },
        });
        const data = await res.json();
        if (data.sharedView) {
          alert("共有中アカウントではこの画面にアクセスできません。");
          window.location.href = "/";
        } else {
          setSharedView(false); // 安全にコード取得開始
        }
      } catch (err) {
        alert("エラーが発生しました");
        window.location.href = "/";
      }
    };
    checkSharedStatus();
  }, []);

  useEffect(() => {
    if (sharedView === false) {
      const fetchCode = async () => {
        setLoading(true);
        try {
          const res = await axios.get("/api/shared/code", {
            headers: {
              Authorization: `Bearer ${sessionStorage.getItem("token")}`,
            },
          });
          setCode(res.data);
        } catch (err) {
          setError("コードの取得に失敗しました。もう一度お試しください。");
        } finally {
          setLoading(false);
        }
      };
      fetchCode();
    }
  }, [sharedView]);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(code);
      setSuccess(true);
      setTimeout(() => setSuccess(false), 2000);
    } catch {
      setError("クリップボードへのコピーに失敗しました。");
    }
  };

  const handleRefresh = async () => {
    setLoading(true);
    try {
      const res = await axios.post(
        "/api/shared/code/refresh",
        {},
        {
          headers: {
            Authorization: `Bearer ${sessionStorage.getItem("token")}`,
          },
        }
      );
      setCode(res.data);
    } catch {
      setError("コードの更新に失敗しました。");
    } finally {
      setLoading(false);
    }
  };

  if (sharedView === null) {
    return (
      <>
        <Header />
        <Container 
          maxWidth="md" 
          sx={{ 
            mt: 4, 
            display: 'flex', 
            justifyContent: 'center',
            // PWAボトムナビゲーション対応
            pb: { xs: 12, sm: 6 },
            minHeight: { xs: 'calc(100vh - 200px)', sm: 'auto' }
          }}
        >
          <CircularProgress color="primary" />
        </Container>
      </>
    );
  }

  return (
    <>
      <Header />
      <Container 
        maxWidth="md" 
        sx={{ 
          mt: 4, 
          // PWAボトムナビゲーション対応：安全マージンを確保
          mb: { xs: 2, sm: 6 }, 
          pb: { xs: 12, sm: 6 }, // モバイルは12（約96px）、デスクトップは6
          px: { xs: 2, sm: 3 },
          // 最小高さを確保してスクロール可能にする
          minHeight: { xs: 'calc(100vh - 200px)', sm: 'auto' }
        }}
      >
        {/* パンくずリスト */}
        <Breadcrumbs 
          separator={<NavigateNextIcon fontSize="small" />} 
          aria-label="breadcrumb"
          sx={{ mb: 3 }}
        >
          <Typography
            component="button"
            sx={{ 
              display: 'flex', 
              alignItems: 'center',
              textDecoration: 'none',
              color: 'inherit',
              cursor: 'pointer',
              background: 'none',
              border: 'none',
              padding: 0,
              font: 'inherit'
            }}
            onClick={() => navigate('/dashboard')}
          >
            <HomeIcon sx={{ mr: 0.5 }} fontSize="inherit" />
            ホーム
          </Typography>
          <Typography
            component="button"
            sx={{ 
              display: 'flex', 
              alignItems: 'center',
              textDecoration: 'none',
              color: 'inherit',
              cursor: 'pointer',
              background: 'none',
              border: 'none',
              padding: 0,
              font: 'inherit'
            }}
            onClick={() => navigate('/setting-Home')}
          >
            <SettingsIcon sx={{ mr: 0.5 }} fontSize="inherit" />
            設定
          </Typography>
          <Typography
            component="button"
            sx={{ 
              display: 'flex', 
              alignItems: 'center',
              textDecoration: 'none',
              color: 'inherit',
              cursor: 'pointer',
              background: 'none',
              border: 'none',
              padding: 0,
              font: 'inherit'
            }}
            onClick={() => navigate('/settings/shared')}
          >
            <PeopleIcon sx={{ mr: 0.5 }} fontSize="inherit" />
            共有設定
          </Typography>
          <Typography
            sx={{ display: 'flex', alignItems: 'center', fontWeight: 500 }}
            color="text.primary"
          >
            <LockIcon sx={{ mr: 0.5 }} fontSize="inherit" />
            共有コードの確認
          </Typography>
        </Breadcrumbs>

        <Fade in={true}>
          <Box>
            {/* ヘッダーセクション */}
            <Box sx={{ mb: 4 }}>
              <Typography variant="h5" fontWeight="bold" gutterBottom>
                共有コードの確認・コピー
              </Typography>
              <Typography color="text.secondary">
                このコードを他のユーザーと共有して、あなたのデータへのアクセスを許可します
              </Typography>
            </Box>

            <Divider sx={{ mb: 4 }} />

            {loading && !code ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
                <CircularProgress />
              </Box>
            ) : error ? (
              <Alert severity="error" sx={{ mb: 3 }}>{error}</Alert>
            ) : (
              <Stack spacing={3}>
                {/* 情報カード */}
                <Paper 
                  elevation={1} 
                  sx={{ 
                    p: 3, 
                    bgcolor: 'info.light', 
                    color: 'info.dark',
                    borderRadius: 2,
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: 2
                  }}
                >
                  <SecurityIcon />
                  <Box>
                    <Typography variant="subtitle1" fontWeight="medium" gutterBottom>
                      セキュリティについて
                    </Typography>
                    <Typography variant="body2">
                      共有コードは定期的に更新することをお勧めします。コードを更新すると、新しい申請には新しいコードが必要になりますが、既存の共有接続は維持されます。
                    </Typography>
                  </Box>
                </Paper>

                {/* コードセクション */}
                <Card 
                  elevation={2} 
                  sx={{ 
                    borderRadius: 2,
                    overflow: 'hidden'
                  }}
                >
                  <Box 
                    sx={{ 
                      px: 3, 
                      py: 2, 
                      bgcolor: 'primary.light',
                      color: 'primary.dark',
                      borderBottom: 1,
                      borderColor: 'divider'
                    }}
                  >
                    <Typography variant="subtitle1" fontWeight="medium">
                      現在の共有コード
                    </Typography>
                  </Box>
                  
                  <CardContent sx={{ p: 3 }}>
                    <TextField
                      fullWidth
                      value={code}
                      InputProps={{ 
                        readOnly: true,
                        sx: { 
                          fontWeight: 'bold', 
                          letterSpacing: 1,
                          fontSize: '1.2rem'
                        }
                      }}
                      sx={{ mb: 2 }}
                    />
                    
                    <Stack 
                      direction={{ xs: 'column', sm: 'row' }} 
                      spacing={2}
                      sx={{ mt: 2 }}
                    >
                      <Button 
                        variant="contained" 
                        onClick={handleCopy}
                        startIcon={<ContentCopyIcon />}
                        fullWidth
                      >
                        コードをコピー
                      </Button>
                      <Button 
                        variant="outlined" 
                        onClick={handleRefresh}
                        startIcon={<RefreshIcon />}
                        fullWidth
                        disabled={loading}
                      >
                        {loading ? 'コード更新中...' : 'コードを更新する'}
                      </Button>
                    </Stack>
                    
                    {success && (
                      <Alert severity="success" sx={{ mt: 2 }}>
                        コードをクリップボードにコピーしました！
                      </Alert>
                    )}
                  </CardContent>
                </Card>
              </Stack>
            )}

            {/* 戻るボタン */}
            <Box sx={{ mt: 4, textAlign: 'center' }}>
              <Button
                variant="outlined"
                startIcon={<ArrowBackIcon />}
                onClick={() => navigate("/settings/shared")}
                size="large"
                sx={{ minWidth: 200 }}
              >
                共有設定に戻る
              </Button>
            </Box>
          </Box>
        </Fade>
      </Container>
    </>
  );
};

export default SettingsSharedCode;